var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import ActionButton from '@rad/components/ui/action-button';
import { PaginatedDataScroller } from '@rad/components/ui/data-scroller';
import { useRouting } from '@rad/hooks/useRouting';
import { useSocketEvent, SOCKET_EVENTS } from '../../common/socket';
import { getNotifications, getUnseenCount, markSeen, markAllSeen } from '../../common/network/notification';
import { formatRelativeTime } from '@rad/utility/formatting';
import { NOTIFICATION_ICONS, getNotificationURL } from '../../types/notification';
const PAGE_SIZE = 15;
const NotificationsBell = () => {
    const [open, setOpen] = React.useState(false);
    const [unseenCount, setUnseenCount] = React.useState(0);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const [, navigate] = useRouting();
    const ref = React.useRef(null);
    // Fetch unseen count on mount
    React.useEffect(() => {
        getUnseenCount().then(data => setUnseenCount(data.count)).catch(() => { });
    }, []);
    // Close on outside click
    React.useEffect(() => {
        const handleClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);
    // WebSocket: new notification
    useSocketEvent(SOCKET_EVENTS.NOTIFICATION_NEW, React.useCallback((_payload) => {
        setUnseenCount(c => c + 1);
        if (open) {
            setRefreshKey(k => k + 1);
        }
    }, [open]));
    const handleMarkAllRead = () => __awaiter(void 0, void 0, void 0, function* () {
        yield markAllSeen();
        setUnseenCount(0);
        setRefreshKey(k => k + 1);
    });
    const handleClickNotification = (n) => {
        if (!n.seen) {
            markSeen(n.id);
            setUnseenCount(c => Math.max(0, c - 1));
        }
        setOpen(false);
        const url = getNotificationURL(n);
        if (url) {
            navigate(url);
        }
    };
    const fetchPage = React.useCallback((page, pageSize) => getNotifications(page, pageSize), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshKey]);
    return (_jsxs("div", { ref: ref, className: "btn-group position-relative", children: [_jsxs("button", { type: "button", className: "btn border-0 p-0 position-relative", style: { color: 'inherit' }, onClick: () => setOpen(o => !o), children: [_jsx("i", { className: "bi bi-bell fs-5", "aria-hidden": "true" }), _jsx("span", { className: "visually-hidden", children: "Notifications " }), unseenCount > 0 && (_jsxs("span", { className: "position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger", children: [unseenCount > 99 ? '99+' : unseenCount, _jsx("span", { className: "visually-hidden", children: " unread notifications" })] }))] }), open && (_jsxs("div", { className: "dropdown-menu show", style: { right: 0, left: 'auto', width: 360, maxHeight: 480, top: '100%', marginTop: 8 }, children: [_jsxs("div", { className: "px-3 py-2 border-bottom d-flex justify-content-between align-items-center", children: [_jsx("h6", { className: "mb-0", children: "Notifications" }), unseenCount > 0 && (_jsx(ActionButton, { className: "btn btn-link btn-sm p-0 text-decoration-none", onClick: handleMarkAllRead, loadingText: "Marking...", children: "Mark all as read" }))] }), _jsx("div", { style: { maxHeight: 400, overflowY: 'auto' }, children: _jsx(PaginatedDataScroller, { fetchData: fetchPage, pageSize: PAGE_SIZE, emptyMessage: _jsx("div", { className: "text-center text-muted py-4", children: "No notifications" }), children: (n) => (_jsxs("button", { type: "button", className: `dropdown-item d-flex align-items-start gap-2 py-2 px-3${!n.seen ? ' bg-light' : ''}`, onClick: () => handleClickNotification(n), style: { whiteSpace: 'normal', cursor: getNotificationURL(n) ? 'pointer' : 'default' }, children: [_jsx("i", { className: `bi ${NOTIFICATION_ICONS[n.notificationType] || 'bi-bell'} mt-1` }), _jsxs("div", { className: "flex-grow-1 overflow-hidden", children: [_jsxs("div", { className: "d-flex justify-content-between align-items-start", children: [_jsx("strong", { className: "small", children: n.title }), !n.seen && _jsx("span", { className: "badge bg-primary rounded-pill ms-2", style: { fontSize: '0.5rem' }, children: "\u00A0" })] }), _jsx("div", { className: "small text-body-secondary", style: { whiteSpace: 'normal' }, children: n.message }), _jsx("small", { className: "text-body-tertiary", children: formatRelativeTime(n.created) })] })] }, n.id)) }, refreshKey) })] }))] }));
};
export default NotificationsBell;
